import "./NominalQuestionEditor.scss";

import { Flex, Tooltip } from "@fluentui/react-northstar";
import FormState, { FieldDescriptor, FieldDescriptors } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { randomString } from "@teams/client-shared";
import { NominalQuestion, NominalQuestionChoice } from "@teams/types";
import React from "react";

import { Button } from "../../../../_shared/components/Button";
import { CheckboxField, TextInputField } from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { QuestionSettings } from "../../../../_shared/types";
import { AuthoringQuestionCollapseButton } from "./AuthoringQuestionCollapseButton";

export const NominalQuestionEditor: React.FC<{
  field: FieldDescriptors<NominalQuestion>;
  questionSettings: QuestionSettings;
  isLimitedEdit?: boolean;
  originalChoices?: string[];
  originalAllowedMultipleChoices?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
}> = ({
  field,
  questionSettings,
  isLimitedEdit,
  originalChoices = [],
  originalAllowedMultipleChoices,
  setCollapsed,
}) => {
  function addChoice() {
    if (field.choices.value.length < SurveyLimits.Questions.Choices.Max) {
      const newChoice: NominalQuestionChoice = { id: randomString(), value: "" };
      if (questionSettings.allowCorrectChoice) {
        newChoice.correct = false;
      }
      field.choices.onChange([...field.choices.value, newChoice]);
    }
  }

  function removeChoice(id: string) {
    if (
      field.allowUserAddOptions.value ||
      field.choices.value.length > SurveyLimits.Questions.Choices.Min
    ) {
      const filteredChoices = field.choices.value.filter(f => f.id !== id);
      if (questionSettings.allowCorrectChoice && !filteredChoices.some(c => c.correct)) {
        filteredChoices[0].correct = true;
      }
      field.choices.onChange([...filteredChoices]);
    }
  }

  const isFieldDisabled =
    !field.allowUserAddOptions.value &&
    field.choices.value.length <= SurveyLimits.Questions.Choices.Min;
  return (
    <Flex column gap="gap.small">
      <div className="nominal-question__form-container">
        <FormState.List field={field.choices} getChildKey={c => c.id}>
          {(choice, index) => {
            const isOriginalChoice = originalChoices.includes(choice.id.value);
            return (
              <Flex
                gap="gap.smaller"
                styles={{ marginTop: index > 0 ? ".75rem" : undefined }}
                vAlign="center"
              >
                {questionSettings.allowCorrectChoice && (
                  <Flex.Item align="center">
                    <Tooltip
                      content={choice.correct?.value ? "Correct choice" : "Make correct choice"}
                      position="after"
                    >
                      <div>
                        <Button
                          className="nominal-question__choice-button"
                          icon={
                            <PollyIcon
                              className={
                                choice.correct?.value
                                  ? "nominal-question__choice-correct"
                                  : "nominal-question__choice-incorrect"
                              }
                              icon={choice.correct?.value ? "correct-icon" : "incorrect-icon"}
                              size="large"
                              inheritColor
                            />
                          }
                          iconOnly
                          text
                          onClick={() => {
                            if (!choice.correct) {
                              return;
                            }

                            // make sure there's at least one correct choice
                            if (choice.correct.value) {
                              if (field.choices.value.filter(c => c.correct).length <= 1) {
                                return;
                              }
                            }

                            if (field.allowMultipleChoices.value) {
                              choice.correct.onChange(!choice.correct.value);
                            } else {
                              field.choices.value.forEach(c => {
                                if (c.id !== choice.id.value && c.correct) {
                                  c.correct = false;
                                } else if (c.id === choice.id.value) {
                                  c.correct = true;
                                }
                              });
                              field.choices.onChange([...field.choices.value]);
                            }
                          }}
                        />
                      </div>
                    </Tooltip>
                  </Flex.Item>
                )}
                <Flex.Item grow align="start">
                  <div>
                    <TextInputField
                      autoFocus={field.choices.value.length > 2}
                      placeholder={`Enter choice ${index + 1}`}
                      field={choice.value}
                      className="nominal-question__choice-input"
                      fluid
                      maxLength={SurveyLimits.Questions.Choices.Value.MaxLength}
                      minLength={SurveyLimits.Questions.Choices.Value.MinLength}
                      required
                      showSuccessIndicator={false}
                      onKeyPress={e => {
                        if (e.key === "Enter") {
                          if (!field.choices.value.some(c => c.value === "")) {
                            addChoice();
                          }
                        }
                      }}
                    />
                  </div>
                </Flex.Item>
                {(!isOriginalChoice || !isLimitedEdit) && (
                  <Flex.Item align="end">
                    <div>
                      <Button
                        className="trash-icon-button"
                        data-tname="nominal-question-delete-choice"
                        tooltip="Delete Choice"
                        disabled={isFieldDisabled}
                        iconOnly
                        onClick={e => {
                          removeChoice(choice.id.value);
                          e.preventDefault();
                        }}
                      >
                        <PollyIcon disabled={isFieldDisabled} icon="trash-icon" inheritColor />
                      </Button>
                    </div>
                  </Flex.Item>
                )}
              </Flex>
            );
          }}
        </FormState.List>
        <Button
          data-tname="nominal-question-add-choice"
          fluid
          className="nominal-question__add-choice-button"
          disabled={field.choices.value.length >= SurveyLimits.Questions.Choices.Max}
          onClick={e => {
            addChoice();
            e.preventDefault();
          }}
          content={<PollyIcon icon="add-choice-icon" ignoreTheme label="Add choice" />}
        />
      </div>

      <Flex vAlign="end" gap="gap.medium" wrap>
        <Flex.Item grow>
          <>
            {!questionSettings.hideRequired && (
              <CheckboxField trueLabel="Required" falseLabel="Required" field={field.required} />
            )}

            {questionSettings.allowMultipleVotes && (
              <CheckboxField
                trueLabel={
                  questionSettings.allowCorrectChoice ? "Multiple correct" : "Allow multiple votes"
                }
                falseLabel={
                  questionSettings.allowCorrectChoice ? "Multiple correct" : "Allow multiple votes"
                }
                field={field.allowMultipleChoices}
                disabled={isLimitedEdit && originalAllowedMultipleChoices}
              />
            )}
            {questionSettings.audienceCanAddChoice && (
              <CheckboxField
                trueLabel="Audience can add choices"
                falseLabel="Audience can add choices"
                field={field.allowUserAddOptions}
                onClick={(_, val) => {
                  if (val && !val.checked) {
                    const choices = field.choices.value;
                    const numMissingChoices = SurveyLimits.Questions.Choices.Min - choices.length;
                    if (numMissingChoices > 0) {
                      const newChoices = [...Array(numMissingChoices)].map(_ => ({
                        id: randomString(),
                        value: "",
                      }));
                      const allChoices = choices.concat(newChoices);
                      field.choices.onChange(allChoices);
                    }
                  }
                }}
              />
            )}

            {questionSettings.enableQuestionComments && (
              <CheckboxField
                field={field.allowComments as FieldDescriptor<boolean>}
                trueLabel="Comments"
                falseLabel="Comments"
                labelProps={{ weight: "regular" }}
              />
            )}
          </>
        </Flex.Item>
      </Flex>

      {questionSettings.enableQuestionComments && field.allowComments?.value && (
        <div>
          <TextInputField
            label="Comment message"
            maxLength={SurveyLimits.CommentAppeal.MaxLength}
            fluid
            field={field.commentAppeal as FieldDescriptor<string>}
            disabled={!field.allowComments?.value}
            labelProps={{ weight: "regular" }}
            placeholder="Please elaborate on your choice"
          />
        </div>
      )}
      {setCollapsed && (
        <Flex fill hAlign="end">
          <AuthoringQuestionCollapseButton setCollapsed={setCollapsed} />
        </Flex>
      )}
    </Flex>
  );
};
